import * as React from 'react';
import { Store } from '../../../../stores';
import { inject, observer } from 'mobx-react';
import { LocalizationService } from '../../../../../../services/LocalizationService';
import { IAToggle } from '../../../../../toggle/Toggle';
import { IATextField } from '../../../../../textField/TextField';
import { IAGroup } from '../../../../../group/Group';
import { IANumberField } from '../../../../../numberField/NumberField';
import { IPlayTimeEditType } from '../../../../../../interfaces/IPlay';
import { IAColorPicker } from '../../../../../..';
import * as styles from '../WidgetEditPanel.css';
import { IACheckbox } from '../../../../../checkbox/Checkbox';
import { IRoom } from '../../../../../../interfaces/IRoom';
import PlayService from '../../../../../../services/PlayService';
import { RotatorViewTemplate } from '../../../rotatorViewSelector/rotatorViewTemplate/RotatorViewTemplate';
import { Helper } from '../../../../../../Helper';

export interface IProps {
  store?: Store;
  forceUpdate: () => void;
  reloadPreview: () => void;
  loadingContent: (loading: boolean) => void
}

export interface IState {
  isLoadingTypes: boolean;
  types: IPlayTimeEditType[];
  showAddFilterPopup: boolean;
  loadingRooms: boolean;
  rooms: IRoom[];
}

@inject('store')
@observer
export class OutlookCalendarWidgetSettings extends React.Component<IProps, IState> {
  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      isLoadingTypes: true,
      types: [],
      showAddFilterPopup: false,
      loadingRooms: false,
      rooms: []
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  componentDidMount() {
    this.getAvailableRooms();
  }

  private async getAvailableRooms() {
    this.setState({ loadingRooms: true });
    let outlookCalendarIntegration = await PlayService.getOutlookCalendarIntegration(this.props.store.environment, this.props.store.token, this.props.store.tenantId, this.props.store.profile.id);
    let rooms: IRoom[] = await PlayService.getOutlookCalendarRooms(this.props.store.environment, this.props.store.token, this.props.store.tenantId);
    this.setState({ rooms: outlookCalendarIntegration?.roomIds.map(id => rooms.find(room => room.id === id)).filter(room => room !== undefined) as IRoom[], loadingRooms: false });
    this.props.store.widgetToEdit.outlookCalendar.roomIds = this.props.store.widgetToEdit.outlookCalendar.rooms?.map(room => room.id);
  }

  private getEvents() {
    this.props.loadingContent(true);
    this.props.store.widgetToEdit.outlookCalendar.events = [];
    this.props.store.widgetToEdit.outlookCalendar.roomIds?.forEach(async roomId => {
      await PlayService.getOutlookCalendarEventsForWidget(this.props.store.environment, this.props.store.token, this.props.store.tenantId, this.state.rooms.find(room => room.id === roomId).emailAddress)
        .then(events => {
          this.props.store.widgetToEdit.outlookCalendar.events = [
            ...(this.props.store.widgetToEdit.outlookCalendar.events || []),
            ...events
          ];
        })
        .finally(() => this.props.loadingContent(false));
    });
  }

  render(): React.ReactNode {
    return (
      <>
        <IAGroup
          label={this.localizationService.strings.PlayAdmin_WidgetSettings?.toUpperCase()}
          darkMode={this.props.store.darkMode}
        >
          <div style={{
            display: "flex",
            flexDirection: "column"
          }}>
            <IATextField
              label={this.localizationService.strings.PlayAdmin_WidgetTitle?.toUpperCase()}
              required
              placeholder={this.localizationService.strings.PlayAdmin_WidgetTitle}
              text={this.props.store.widgetToEdit.title}
              darkMode={this.props.store.darkMode}
              highlightColor={this.props.store.highlightColor}
              borderRadius={5}
              labelStyle={{
                fontSize: "12px"
              }}
              onChange={(title: string) => {
                this.props.store.widgetToEdit.title = title;
                this.props.forceUpdate();
              }}
            />
            <IAToggle
              style={{
                marginTop: 20,
                fontSize: 12
              }}
              darkMode={this.props.store.darkMode}
              label={this.localizationService.strings.PlayAdmin_ShowWidgetTitle?.toUpperCase()}
              checked={this.props.store.widgetToEdit.showTitle}
              color={this.props.store.highlightColor}
              onChange={() => {
                this.props.store.widgetToEdit.showTitle = !!!this.props.store.widgetToEdit.showTitle;
                this.props.forceUpdate();
              }}
            />
            <IANumberField
              label={this.localizationService.strings.PlayAdmin_MinimumDuration?.toUpperCase()}
              number={this.props.store.widgetToEdit?.duration}
              darkMode={this.props.store.darkMode}
              highlightColor={this.props.store.highlightColor}
              borderRadius={5}
              labelStyle={{
                fontSize: "12px"
              }}
              onChange={(duration: number) => {
                this.props.store.widgetToEdit.duration = duration;
                this.props.forceUpdate();
              }}
              styles={{
                width: "100%",
                clear: "both"
              }}
            />
            <div className={`${styles.IA_outlookCalendarSelector} ${this.props.store.darkMode ? styles.dark : ''}`}>
              <h4>{this.localizationService.strings.PlayAdmin_OutlookCalendarSelectCalendars}</h4>
              {this.state.rooms?.sort((a, b) => a.displayName.localeCompare(b.displayName)).map((room) => {
                return <div key={room.id} className={styles.IA_outlookCalendarSelectorRow}>
                  <div>
                    {room.displayName}
                  </div>
                  <IACheckbox
                    highlightColor={this.props.store.highlightColor}
                    isChecked={this.props.store.widgetToEdit.outlookCalendar.roomIds?.some(id => id === room.id)}
                    onChange={(isChecked) => {
                      if (isChecked) {
                        this.props.store.widgetToEdit.outlookCalendar.roomIds?.push(room.id);
                      } else {
                        const index = this.props.store.widgetToEdit.outlookCalendar.roomIds?.findIndex(id => id === room.id);
                        if (index !== -1) {
                          this.props.store.widgetToEdit.outlookCalendar.roomIds?.splice(index, 1);
                        }
                      }
                      this.getEvents();
                      this.props.forceUpdate();
                    }}
                  />
                </div>
              })
              }
            </div>
          </div>
        </IAGroup>
        <IAGroup
          label={this.localizationService.strings.PlayAdmin_Layout?.toUpperCase()}
          darkMode={this.props.store.darkMode}
        >
          <div style={{
            display: "flex",
            flexDirection: "column"
          }}>
            <IATextField
              label={this.localizationService.strings.PlayAdmin_Headline?.toUpperCase()}
              placeholder={this.localizationService.strings.PlayAdmin_Headline}
              text={this.props.store.widgetToEdit.outlookCalendar.headline}
              darkMode={this.props.store.darkMode}
              highlightColor={this.props.store.highlightColor}
              borderRadius={5}
              labelStyle={{
                fontSize: "12px"
              }}
              onChange={(headline: string) => {
                this.props.store.widgetToEdit.outlookCalendar.headline = headline;
                this.props.forceUpdate();
              }}
            />
            <IATextField
              label={this.localizationService.strings.PlayAdmin_OutlookCalendarNoEventsLabelTitle?.toUpperCase()}
              placeholder={this.localizationService.strings.PlayAdmin_OutlookCalendarNoEventsLabel}
              text={this.props.store.widgetToEdit.outlookCalendar.noEventsLabel ?? this.localizationService.strings.PlayAdmin_OutlookCalendarNoEventsLabel}
              darkMode={this.props.store.darkMode}
              highlightColor={this.props.store.highlightColor}
              borderRadius={5}
              labelStyle={{
                fontSize: "12px"
              }}
              onChange={(noEventsLabel: string) => {
                this.props.store.widgetToEdit.outlookCalendar.noEventsLabel = noEventsLabel;
                this.props.forceUpdate();
              }}
            />
            <div style={{ fontSize: "12px", width: "100%", float: "left", marginTop: 20, marginBottom: 10, color: this.props.store.darkMode ? Helper.darkModeLabelColor : 'rgb(51, 51, 51)' }}>{this.localizationService.strings.PlayAdmin_ViewType?.toUpperCase()}</div>
            <div style={{
              clear: "both",
              width: 330,
              float: "left"
            }}>
              <RotatorViewTemplate
                template={"list"}
                selected={true}
                listBackgroundColor={"#ffffff"}
                listTextColor={"#333333"}
                onSelect={() => { }}
              />
            </div>
            <IAToggle
              style={{
                marginTop: 20,
                fontSize: 12
              }}
              darkMode={this.props.store.darkMode}
              label={this.localizationService.strings.PlayAdmin_OutlookCalendarShowAll?.toUpperCase()}
              checked={this.props.store.widgetToEdit.outlookCalendar.showAll}
              color={this.props.store.highlightColor}
              onChange={() => {
                this.props.store.widgetToEdit.outlookCalendar.showAll = this.props.store.widgetToEdit.outlookCalendar.showAll != undefined ? !this.props.store.widgetToEdit.outlookCalendar.showAll : true;
                this.props.forceUpdate();
              }}
            />
            <div className={styles.IA_info}>{this.localizationService.strings.PlayAdmin_OutlookCalendarShowAllInfo}</div>
            <IAColorPicker
              key="IA_themeBackgroundColor"
              label={this.localizationService.strings.PlayAdmin_BackgroundColor.toUpperCase()}
              color={this.props.store.widgetToEdit.outlookCalendar.backgroundColor}
              swatches={this.props.store.profile?.swatches}
              allowAddingToSwatches={false}
              inTeams
              onChange={(backgroundColor: string) => {
                this.props.store.widgetToEdit.outlookCalendar.backgroundColor = backgroundColor;
                this.props.forceUpdate();
              }}
              darkMode={this.props.store.darkMode}
              showClearButton={false}
              styles={{
                marginTop: 20,
                float: "left",
                width: "auto",
              }}
            />
            <IAColorPicker
              key="IA_themeBackgroundColor"
              label={this.localizationService.strings.PlayAdmin_HeadlineColor.toUpperCase()}
              color={this.props.store.widgetToEdit.outlookCalendar.headlineColor}
              swatches={this.props.store.profile?.swatches}
              allowAddingToSwatches={false}
              inTeams
              onChange={(headlineColor: string) => {
                this.props.store.widgetToEdit.outlookCalendar.headlineColor = headlineColor;
                this.props.forceUpdate();
              }}
              darkMode={this.props.store.darkMode}
              showClearButton={false}
              styles={{
                marginTop: 20,
                float: "left",
                width: "auto",
              }}
            />
            <IAColorPicker
              key="IA_themeTextColor"
              label={this.localizationService.strings.PlayAdmin_TextColor.toUpperCase()}
              color={this.props.store.widgetToEdit.outlookCalendar.textColor}
              swatches={this.props.store.profile?.swatches}
              allowAddingToSwatches={false}
              inTeams
              onChange={(textColor: string) => {
                this.props.store.widgetToEdit.outlookCalendar.textColor = textColor;
                this.props.forceUpdate();
              }}
              darkMode={this.props.store.darkMode}
              showClearButton={false}
              styles={{
                marginTop: 20,
                float: "left",
                width: "auto",
              }}
            />
            <IAColorPicker
              key="IA_themeHighlightColor"
              label={this.localizationService.strings.PlayAdmin_HighlightColor.toUpperCase()}
              color={this.props.store.widgetToEdit.outlookCalendar.highlightColor}
              swatches={this.props.store.profile?.swatches}
              allowAddingToSwatches={false}
              inTeams
              onChange={(highlightColor: string) => {
                this.props.store.widgetToEdit.outlookCalendar.highlightColor = highlightColor;
                this.props.forceUpdate();
              }}
              darkMode={this.props.store.darkMode}
              showClearButton={false}
              styles={{
                marginTop: 20,
                float: "left",
                width: "auto",
              }}
            />
            <IAToggle
              style={{
                marginTop: 20,
                fontSize: 12
              }}
              darkMode={this.props.store.darkMode}
              label={this.localizationService.strings.PlayAdmin_OutlookCalendarShowDate?.toUpperCase()}
              checked={this.props.store.widgetToEdit.outlookCalendar.showDate}
              color={this.props.store.highlightColor}
              onChange={() => {
                this.props.store.widgetToEdit.outlookCalendar.showDate = this.props.store.widgetToEdit.outlookCalendar.showDate != undefined ? !this.props.store.widgetToEdit.outlookCalendar.showDate : true;
                this.props.forceUpdate();
              }}
            />
            <IAToggle
              style={{
                marginTop: 20,
                fontSize: 12
              }}
              darkMode={this.props.store.darkMode}
              label={this.localizationService.strings.PlayAdmin_OutlookCalendarShowTime?.toUpperCase()}
              checked={this.props.store.widgetToEdit.outlookCalendar.showTime}
              color={this.props.store.highlightColor}
              onChange={() => {
                this.props.store.widgetToEdit.outlookCalendar.showTime = this.props.store.widgetToEdit.outlookCalendar.showTime != undefined ? !this.props.store.widgetToEdit.outlookCalendar.showTime : true;
                this.props.forceUpdate();
              }}
            />
            <IAToggle
              style={{
                marginTop: 20,
                fontSize: 12
              }}
              darkMode={this.props.store.darkMode}
              label={this.localizationService.strings.PlayAdmin_OutlookCalendarShowOrganizer?.toUpperCase()}
              checked={this.props.store.widgetToEdit.outlookCalendar.showOrganizer}
              color={this.props.store.highlightColor}
              onChange={() => {
                this.props.store.widgetToEdit.outlookCalendar.showOrganizer = this.props.store.widgetToEdit.outlookCalendar.showOrganizer != undefined ? !this.props.store.widgetToEdit.outlookCalendar.showOrganizer : true;
                this.props.forceUpdate();
              }}
            />
            <IAToggle
              style={{
                marginTop: 20,
                fontSize: 12
              }}
              darkMode={this.props.store.darkMode}
              label={this.localizationService.strings.PlayAdmin_OutlookCalendarShowRoom?.toUpperCase()}
              checked={this.props.store.widgetToEdit.outlookCalendar.showRoom}
              color={this.props.store.highlightColor}
              onChange={() => {
                this.props.store.widgetToEdit.outlookCalendar.showRoom = this.props.store.widgetToEdit.outlookCalendar.showRoom != undefined ? !this.props.store.widgetToEdit.outlookCalendar.showRoom : true;
                this.props.forceUpdate();
              }}
            />
            <IAToggle
              style={{
                marginTop: 20,
                fontSize: 12
              }}
              darkMode={this.props.store.darkMode}
              label={this.localizationService.strings.PlayAdmin_OutlookCalendarShowMeetingTitle?.toUpperCase()}
              checked={this.props.store.widgetToEdit.outlookCalendar.showMeetingTitle}
              color={this.props.store.highlightColor}
              onChange={() => {
                this.props.store.widgetToEdit.outlookCalendar.showMeetingTitle = this.props.store.widgetToEdit.outlookCalendar.showMeetingTitle != undefined ? !this.props.store.widgetToEdit.outlookCalendar.showMeetingTitle : true;
                this.props.forceUpdate();
              }}
            />
          </div>
        </IAGroup>
      </>
    );
  }
}
